import { useMemo, useRef } from 'react'
import ReactFlow, {
  Background,
  BackgroundVariant,
  Controls,
  Edge,
  Handle,
  Node,
  Position,
  ReactFlowInstance,
} from 'reactflow'
import 'reactflow/dist/style.css'
import { Button, Flex, TabFooter } from 'styling/GlobalStyles'
import { IProcessTemplateTab, IPTStateFunction } from 'types/processTemplates'
import { ItemsPick } from 'components/ItemsPick/ItemsPick'
import { ClearButton } from 'components/Button/ClearButton'
import { CancelBtn } from 'components/Button/CancelBtn'
import {
  TaskNodeContainer,
  TaskNodeTitle,
  WorkflowDropEmptyHint,
  WorkflowLogicContainer,
} from 'pages/Workflows/Workflows.styled'

const INPUT_X = 40
const FUNCTION_X = 360
const OUTPUT_X = 680
const ROW_GAP = 120

const ReadOnlyInputNode = ({ data }: { data: { name: string } }) => (
  <TaskNodeContainer root>
    <Handle type='source' position={Position.Right} isConnectable={false} />
    <TaskNodeTitle>{data.name}</TaskNodeTitle>
  </TaskNodeContainer>
)

const ReadOnlyFunctionNode = ({ data }: { data: { name: string } }) => (
  <TaskNodeContainer root>
    <Handle type='target' position={Position.Left} isConnectable={false} />
    <TaskNodeTitle>{data.name}</TaskNodeTitle>
    <Handle type='source' position={Position.Right} isConnectable={false} />
  </TaskNodeContainer>
)

const ReadOnlyOutputNode = ({ data }: { data: { name: string } }) => (
  <TaskNodeContainer>
    <Handle type='target' position={Position.Left} isConnectable={false} />
    <TaskNodeTitle>{data.name}</TaskNodeTitle>
  </TaskNodeContainer>
)

const nodeTypes = {
  readOnlyInput: ReadOnlyInputNode,
  readOnlyFunction: ReadOnlyFunctionNode,
  readOnlyOutput: ReadOnlyOutputNode,
}

export const ProcessTemplatesLogicTab = ({ state, setState, goToData, view }: IProcessTemplateTab) => {
  const flowRef = useRef<ReactFlowInstance | null>(null)

  const handleAddFunc = (fn: IPTStateFunction) => {
    setState(prev => {
      const copy = structuredClone(prev)
      copy.processFunction = fn
      return copy
    })
  }

  const clearLogic = () => {
    setState(prev => {
      const copy = structuredClone(prev)
      copy.processFunction = null
      return copy
    })
  }

  const nodes = useMemo<Node[]>(() => {
    if (!state.processFunction) return []

    const inputNodes: Node[] = state.processFunction.inputs.map((input, index) => ({
      id: `input-${input.component}-${input.index}`,
      type: 'readOnlyInput',
      position: { x: INPUT_X, y: index * ROW_GAP },
      data: { name: input.name },
      draggable: false,
      selectable: false,
    }))

    const functionY =
      Math.max(0, Math.floor((Math.max(state.processFunction.inputs.length, state.processFunction.outputs.length) - 1) / 2)) * ROW_GAP

    const functionNode: Node = {
      id: `function-${state.processFunction.pfid}`,
      type: 'readOnlyFunction',
      position: { x: FUNCTION_X, y: functionY },
      data: { name: state.processFunction.name },
      draggable: false,
      selectable: false,
    }

    const outputNodes: Node[] = state.processFunction.outputs.map((output, index) => ({
      id: `output-${output.component}-${output.index}`,
      type: 'readOnlyOutput',
      position: { x: OUTPUT_X, y: index * ROW_GAP },
      data: { name: output.componentGivenName || output.name || `Output ${output.index}` },
      draggable: false,
      selectable: false,
    }))

    return [...inputNodes, functionNode, ...outputNodes]
  }, [state.processFunction])

  const edges = useMemo<Edge[]>(() => {
    if (!state.processFunction) return []

    const inputEdges: Edge[] = state.processFunction.inputs.map((input) => ({
      id: `edge-input-${input.component}-${input.index}`,
      source: `input-${input.component}-${input.index}`,
      target: `function-${state.processFunction!.pfid}`,
      type: 'smoothstep',
      animated: false,
      selectable: false,
      style: { stroke: '#ff9900', strokeWidth: 2 },
    }))

    const outputEdges: Edge[] = state.processFunction.outputs.map((output) => ({
      id: `edge-output-${output.component}-${output.index}`,
      source: `function-${state.processFunction!.pfid}`,
      target: `output-${output.component}-${output.index}`,
      type: 'smoothstep',
      animated: false,
      selectable: false,
      style: { stroke: '#ff9900', strokeWidth: 2 },
    }))

    return [...inputEdges, ...outputEdges]
  }, [state.processFunction])

  return (
    <>
      <Flex between gap='2rem' height='90%'>
        <WorkflowLogicContainer>
          <WorkflowDropEmptyHint visible={!state.processFunction}>
            Select a function from the right panel to preview process logic
          </WorkflowDropEmptyHint>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            onInit={(instance) => {
              flowRef.current = instance
              if (nodes.length > 0) instance.fitView({ padding: 0.2 })
            }}
            fitView
            minZoom={0.2}
            maxZoom={1.5}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={false}
            edgesFocusable={false}
            nodesFocusable={false}
            panOnDrag
            zoomOnScroll
            zoomOnPinch
            zoomOnDoubleClick={false}
            proOptions={{ hideAttribution: true }}
          >
            <Background variant={BackgroundVariant.Dots} gap={20} size={1} />
            <Controls showInteractive={false} />
          </ReactFlow>
        </WorkflowLogicContainer>
        <ItemsPick
          link='process/function/configurations'
          width='30%'
          selectedList={state?.processFunction?.name ? [{ name: state?.processFunction?.name, pfid: state?.processFunction?.pfid }] : []}
          oneColumn
          noScroll
          parserFunction={resp => resp}
          handleAdd={handleAddFunc}
          placeholder='Search Functions'
          customSelectedProp='pfid'
          view={view}
        />
      </Flex>
      {!view && <TabFooter>
        <Flex gap='1rem'>
          <ClearButton text='Clear logic' onClick={clearLogic} />
          <CancelBtn />
          <Button orange onClick={goToData}>
            Configure Data
          </Button>
        </Flex>
      </TabFooter>}
    </>
  )
}